/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { AddPayslipSchema, type AddPayslipInput } from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { useTherapists } from '../../lib/queries/clinical.js';
import { useAddPayslip } from '../../lib/queries/payroll.js';
import { rupeesToPaise } from '../../lib/utils/money.js';

interface PayslipFormProps {
  readonly runId: string;
  readonly onDone: () => void;
}

const SELECT_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';

/** "Add Payslip" form (Figma). Validated by shared `AddPayslipSchema`. */
export function PayslipForm({ runId, onDone }: PayslipFormProps): JSX.Element {
  const { t } = useTranslation();
  const therapists = useTherapists();
  const add = useAddPayslip();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AddPayslipInput>({ resolver: zodResolver(AddPayslipSchema) });

  const submit = useCallback(
    async (values: AddPayslipInput): Promise<void> => {
      await add.mutateAsync({
        runId,
        payload: {
          therapistId: values.therapistId,
          grossPaise: values.grossPaise,
          ...(values.sessionsCount !== undefined ? { sessionsCount: values.sessionsCount } : {}),
          ...(values.notes !== undefined && values.notes !== '' ? { notes: values.notes } : {}),
        },
      });
      onDone();
    },
    [add, runId, onDone],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <div>
        <label htmlFor="payslip-therapist" className={LABEL_CLASS}>
          {t('payroll.form.therapist')}
        </label>
        <select
          id="payslip-therapist"
          className={SELECT_CLASS}
          defaultValue=""
          aria-invalid={errors.therapistId !== undefined}
          {...register('therapistId')}
        >
          <option value="" disabled>
            {t('payroll.form.selectTherapist')}
          </option>
          {(therapists.data ?? []).map((th) => (
            <option key={th.id} value={th.id}>
              {th.fullName}
            </option>
          ))}
        </select>
        {errors.therapistId !== undefined && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            {errors.therapistId.message}
          </p>
        )}
      </div>

      <FormField
        id="payslip-gross"
        type="number"
        label={t('payroll.form.gross')}
        placeholder={t('form.invoice.amountHint')}
        autoComplete="off"
        inputMode="numeric"
        error={errors.grossPaise?.message}
        registration={register('grossPaise', {
          setValueAs: (v: string) => rupeesToPaise(Number(v)),
        })}
      />
      <FormField
        id="payslip-sessions"
        type="number"
        label={t('payroll.form.sessions')}
        autoComplete="off"
        inputMode="numeric"
        error={errors.sessionsCount?.message}
        registration={register('sessionsCount')}
      />

      {add.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || add.isPending}>
          {t('payroll.form.addSubmit')}
        </Button>
      </div>
    </form>
  );
}
